import React, {
  useContext,
  useEffect,
  useState,
  FunctionComponent,
} from 'react'
import accounting from 'accounting'
import { get } from 'lodash'
import { DateTime } from 'luxon'
import { decode } from 'he'
import { DateTimeDisplay, PageHeading } from '../../../components'
import Table from '../../../components/Table/next'
import ReportActions from '../../../components/ReportActions'
import { useSelector } from 'react-redux'
import { State } from '../../../reducers'
import { UserStore } from '../../../typings/stores/UserStore'
import { ClicksTableContext } from './index'
import { ClickDetail } from '../../../typings/detailReport'
import { prepareDatesForApi } from '../../../utils/dates'
import { ClickDetailReportVariables } from './index'
import TableFilters from '../Filters/TableFilters'
import { dumpData } from '../../../actions/reports'

interface ClicksTableProps {
  data: any
  loading: boolean
  error: boolean
  getData: ({ variables }) => void
  exportData: any
  exportLoading: boolean
  exportError: boolean
  getExportData: ({ variables }) => void
}

const ClicksTable: FunctionComponent<ClicksTableProps> = ({
  getData,
  data,
  loading,
  error,
  getExportData,
  exportData,
  exportLoading,
  exportError,
}) => {
  const {
    filters,
    pagination: { size, page },
    sort,
    changePageSize,
    changePage,
    changeSort,
  } = useContext(ClicksTableContext)

  const user = useSelector<State, UserStore>(state => state.user)
  const [isExporting, setIsExporting] = useState(false)
  const [pointer, setPointer] = useState(0)
  const [dataToExport, setDataToExport] = useState([])
  const recordsPerPage = 10000

  const updateVariables = () => {
    return {
      ...prepareDatesForApi(filters.startDate, filters.endDate),
      skip: (page - 1) * size,
      take: size,
      sorts: sort.by && [
        {
          [sort.by]: sort.dir.toUpperCase(),
        },
      ],
      externalPropertyId: user.activeWebsite.id.toString(),
      externalVerticalId: filters.vertical,
      externalProductTypeId: parseInt(filters.productType),
      externalProgramId: filters.program,
      externalDisplayedProductId: filters.product,
      externalClickedProductId: filters.clickedProduct,
      externalCampaignId: filters.campaign,
      subTracking: filters.subTracking,
    } as ClickDetailReportVariables
  }

  const fetchData = () => {
    getData({ variables: updateVariables() })
    updateTableColumns()
  }

  const fetchExport = () => {
    setDataToExport([])
    setPointer(0)
    setIsExporting(true)
    getExportData({
      variables: {
        ...updateVariables(),
        take: recordsPerPage,
      },
    })
  }

  useEffect(() => {
    const pagedData = exportData?.clickDetailReporting?.page
    if (pagedData) {
      setDataToExport(v => [...v, ...pagedData])
      setPointer(v => v + pagedData.length)
    }
  }, [exportData])

  useEffect(() => {
    if (
      isExporting &&
      pointer < get(data, `clickDetailReporting.pagination.totalEntries`, 0)
    ) {
      getExportData({
        variables: {
          ...updateVariables(),
          skip: pointer,
          take: recordsPerPage,
        },
      })
    } else {
      if (dataToExport.length) {
        dumpData('clickDetails', dataToExport, {
          ...filters,
        })
        setIsExporting(false)
        setDataToExport([])
        setPointer(0)
      }
    }
  }, [pointer])

  useEffect(fetchData, [sort, page, size, user.activeWebsite])

  const pagination = {
    currentPage: get(data, `clickDetailReporting.pagination.currentPage`, 1),
    pageSize: get(data, `clickDetailReporting.pagination.pageSize`, 10),
    total: get(data, `clickDetailReporting.pagination.totalEntries`, 0),
  }

  const allColumns = [
    {
      text: 'Click Date',
      sortable: true,
      field: 'clickDate',
      render: row =>
        row.clickDate && (
          <DateTimeDisplay datetime={DateTime.fromISO(row.clickDate)} />
        ),
    },
    {
      text: 'Click ID',
      sortable: true,
      field: 'clickId',
    },
    {
      text: 'Product Type',
      sortable: true,
      field: 'productTypeName',
      render: row => row.productTypeName && decode(row.productTypeName),
    },
    {
      text: 'Program',
      sortable: true,
      field: 'advertiserName',
    },
    {
      text: 'Product ID',
      sortable: true,
      field: 'clickedProductId',
    },
    {
      text: 'Product',
      sortable: true,
      field: 'clickedProductName',
      render: row => row.clickedProductName && decode(row.clickedProductName),
    },
    {
      text: 'Campaign',
      sortable: true,
      field: 'campaignName',
      render: row => row.campaignName && decode(row.campaignName),
    },
    {
      text: 'Page',
      sortable: true,
      field: 'pagePath',
      render: row => row.pagePath && decode(row.pagePath),
    },
    {
      text: 'State',
      sortable: true,
      field: 'state',
      render: row => row.state && decode(row.state),
    },
    {
      text: 'Sub ID',
      sortable: true,
      field: 'subTracking',
    },
    {
      text: 'Sales',
      sortable: true,
      field: 'sales',
    },
    {
      text: 'Commissions',
      sortable: true,
      field: 'commission',
      render: ({ commissions }) => accounting.formatMoney(commissions, '$'),
    },
  ]

  const [tableColumns, setTableColumns] = useState(allColumns)
  const [propertyHasData, setPropertyHasData] = useState(true)
  const [pageDescription, setPageDescription] = useState(
    'This report contains click details for the filters selected.'
  )

  useEffect(() => {
    if (!propertyHasData) {
      setPageDescription('No reporting data available for this property yet.')
    }
  }, [propertyHasData])

  useEffect(() => {
    if (!propertyHasData) {
      window.location.reload()
    }
  }, [user.activeWebsite])

  const updateTableColumns = () => {
    if (filters.vertical === '3') {
      // Mortgages
      const availableColumns = [
        'clickDate',
        'clickId',
        'productTypeName',
        'clickedProductName',
        'clickedProductId',
        'campaignName',
        'pagePath',
        'state',
        'subTracking',
        'sales',
        'commissions',
      ]
      setTableColumns(
        allColumns.filter(c => availableColumns.includes(c.field))
      )
    } else if (
      filters.vertical === '5' ||
      filters.vertical === '7' ||
      filters.vertical === '8' ||
      filters.vertical === '10' ||
      filters.vertical === '11' ||
      filters.vertical === '13'
    ) {
      // Credit Cards || Investing || Health || Retail || Home Services || Personal Finance
      const availableColumns = [
        'clickDate',
        'clickId',
        'productTypeName',
        'programName',
        'clickedProductName',
        'clickedProductId',
        'campaignName',
        'pagePath',
        'subTracking',
        'sales',
        'commissions',
      ]
      setTableColumns(
        allColumns.filter(c => availableColumns.includes(c.field))
      )
    } else if (
      filters.vertical === '4' ||
      filters.vertical === '6' ||
      filters.vertical === '9' ||
      filters.vertical === '12'
    ) {
      // Loans || Deposits || Insurance || Home Equity
      const availableColumns = [
        'clickDate',
        'clickId',
        'productTypeName',
        'campaignName',
        'pagePath',
        'subTracking',
        'sales',
        'commissions',
      ]
      setTableColumns(
        allColumns.filter(c => availableColumns.includes(c.field))
      )
    } else {
      setTableColumns(allColumns)
    }
  }

  return (
    <>
      <PageHeading title="Click Details" description={pageDescription}>
        {propertyHasData && (
          <TableFilters
            tableContext={ClicksTableContext}
            showClickedProducts={true}
            setPropertyHasData={setPropertyHasData}
          />
        )}
      </PageHeading>
      {propertyHasData && (
        <>
          <ReportActions
            fetchReport={fetchData}
            fetchExport={fetchExport}
            disabled={
              !get(data, `clickDetailReporting.pagination.totalEntries`, 0)
            }
            reportStatus={{ loading: loading, hasError: error }}
            exportStatus={{ loading: exportLoading, hasError: exportError }}
          />
          <Table<ClickDetail[]>
            cols={tableColumns}
            data={data && get(data, `clickDetailReporting.page`)}
            onSortChange={changeSort}
            sort={sort}
            pagination={{
              onPageSizeUpdate: changePageSize,
              onPaginate: changePage,
              ...pagination,
            }}
            isLoading={loading}
          />
        </>
      )}
    </>
  )
}

export default ClicksTable
